import type { UserRole, Permission, RolePermissions } from '../types';

// Default role permissions mapping
export const DEFAULT_ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  system_admin: {
    role: 'system_admin',
    description: 'Full system access with all permissions',
    isCustom: false,
    permissions: [
      // All system permissions
      'system:manage_users',
      'system:manage_roles',
      'system:manage_permissions',
      'system:view_audit_logs',
      'system:manage_settings',
      'system:manage_integrations',
      'system:backup_restore',
      'system:view_analytics',
      // All event permissions
      'events:create',
      'events:edit',
      'events:delete',
      'events:view',
      'events:close',
      'events:reopen',
      'events:assign_personnel',
      'events:manage_resources',
      // All incident permissions
      'incidents:create',
      'incidents:edit',
      'incidents:delete',
      'incidents:view',
      'incidents:assign',
      'incidents:close',
      'incidents:reopen',
      'incidents:change_severity',
      'incidents:change_category',
      'incidents:add_notes',
      'incidents:view_sensitive',
      'incidents:export',
      // All major incident permissions
      'major_incidents:declare',
      'major_incidents:manage',
      'major_incidents:view',
      'major_incidents:assign_commanders',
      'major_incidents:close',
      'major_incidents:edit_methan',
      // All resource permissions
      'resources:create',
      'resources:edit',
      'resources:delete',
      'resources:view',
      'resources:assign',
      'resources:recall',
      'resources:change_status',
      'resources:manage_inventory',
      // All unit permissions
      'units:create',
      'units:edit',
      'units:delete',
      'units:view',
      'units:dispatch',
      'units:recall',
      'units:change_status',
      'units:assign_personnel',
      'units:view_location',
      // All personnel permissions
      'personnel:create',
      'personnel:edit',
      'personnel:delete',
      'personnel:view',
      'personnel:assign',
      'personnel:change_status',
      'personnel:view_sensitive',
      'personnel:manage_certifications',
      // All communications permissions
      'comms:send_messages',
      'comms:broadcast',
      'comms:view_history',
      'comms:manage_channels',
      'comms:emergency_alert',
      'comms:priority_override',
      // All map permissions
      'map:view',
      'map:draw',
      'map:edit_drawings',
      'map:delete_drawings',
      'map:share_drawings',
      'map:view_unit_locations',
      'map:view_sensitive_locations',
      // All reports permissions
      'reports:view',
      'reports:create',
      'reports:export',
      'reports:view_sensitive',
      'reports:manage_templates',
      // All logs permissions
      'logs:view',
      'logs:create',
      'logs:edit',
      'logs:delete',
      'logs:export',
      'logs:view_audit',
      // All notifications permissions
      'notifications:send',
      'notifications:broadcast',
      'notifications:manage',
      // All training permissions
      'training:view',
      'training:create',
      'training:manage',
      'training:assign',
      // All intelligence permissions
      'intelligence:view',
      'intelligence:create',
      'intelligence:edit',
      'intelligence:delete',
      'intelligence:share',
      'prfs:view',
      'prfs:print',
      'prfs:export',
      'prfs:edit',
      'prfs:delete',
    ],
  },

  command_admin: {
    role: 'command_admin',
    description: 'Command center administrator with full operational control',
    isCustom: false,
    permissions: [
      'system:view_audit_logs',
      'system:view_analytics',
      'events:create',
      'events:edit',
      'events:delete',
      'events:view',
      'events:close',
      'events:reopen',
      'events:assign_personnel',
      'events:manage_resources',
      'incidents:create',
      'incidents:edit',
      'incidents:delete',
      'incidents:view',
      'incidents:assign',
      'incidents:close',
      'incidents:reopen',
      'incidents:change_severity',
      'incidents:change_category',
      'incidents:add_notes',
      'incidents:view_sensitive',
      'incidents:export',
      'major_incidents:declare',
      'major_incidents:manage',
      'major_incidents:view',
      'major_incidents:assign_commanders',
      'major_incidents:close',
      'major_incidents:edit_methan',
      'resources:create',
      'resources:edit',
      'resources:view',
      'resources:assign',
      'resources:recall',
      'resources:change_status',
      'resources:manage_inventory',
      'units:create',
      'units:edit',
      'units:view',
      'units:dispatch',
      'units:recall',
      'units:change_status',
      'units:assign_personnel',
      'units:view_location',
      'personnel:view',
      'personnel:assign',
      'personnel:change_status',
      'comms:send_messages',
      'comms:broadcast',
      'comms:view_history',
      'comms:manage_channels',
      'comms:emergency_alert',
      'comms:priority_override',
      'map:view',
      'map:draw',
      'map:edit_drawings',
      'map:delete_drawings',
      'map:share_drawings',
      'map:view_unit_locations',
      'map:view_sensitive_locations',
      'reports:view',
      'reports:create',
      'reports:export',
      'reports:view_sensitive',
      'logs:view',
      'logs:create',
      'logs:edit',
      'logs:export',
      'logs:view_audit',
      'notifications:send',
      'notifications:broadcast',
      'notifications:manage',
      'intelligence:view',
      'intelligence:create',
      'intelligence:edit',
      'intelligence:share',
      'prfs:view',
      'prfs:print',
      'prfs:export',
      'prfs:edit',
    ],
  },

  command_supervisor: {
    role: 'command_supervisor',
    description: 'Command center supervisor with operational oversight',
    isCustom: false,
    permissions: [
      'events:view',
      'events:edit',
      'events:assign_personnel',
      'events:manage_resources',
      'incidents:create',
      'incidents:edit',
      'incidents:view',
      'incidents:assign',
      'incidents:close',
      'incidents:change_severity',
      'incidents:change_category',
      'incidents:add_notes',
      'incidents:export',
      'major_incidents:view',
      'major_incidents:manage',
      'major_incidents:assign_commanders',
      'resources:view',
      'resources:assign',
      'resources:recall',
      'resources:change_status',
      'units:view',
      'units:dispatch',
      'units:recall',
      'units:change_status',
      'units:assign_personnel',
      'units:view_location',
      'personnel:view',
      'personnel:assign',
      'personnel:change_status',
      'comms:send_messages',
      'comms:broadcast',
      'comms:view_history',
      'comms:emergency_alert',
      'map:view',
      'map:draw',
      'map:edit_drawings',
      'map:share_drawings',
      'map:view_unit_locations',
      'reports:view',
      'reports:create',
      'reports:export',
      'logs:view',
      'logs:create',
      'logs:export',
      'notifications:send',
      'notifications:broadcast',
      'intelligence:view',
      'intelligence:create',
      'prfs:view',
      'prfs:print',
      'prfs:export',
    ],
  },

  command_dispatcher: {
    role: 'command_dispatcher',
    description: 'Command center dispatcher for incident and resource coordination',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:create',
      'incidents:edit',
      'incidents:view',
      'incidents:assign',
      'incidents:add_notes',
      'major_incidents:view',
      'resources:view',
      'resources:assign',
      'resources:change_status',
      'units:view',
      'units:dispatch',
      'units:change_status',
      'units:view_location',
      'personnel:view',
      'personnel:assign',
      'comms:send_messages',
      'comms:view_history',
      'map:view',
      'map:draw',
      'map:view_unit_locations',
      'reports:view',
      'logs:view',
      'logs:create',
      'notifications:send',
      'intelligence:view',
    ],
  },

  command_operator: {
    role: 'command_operator',
    description: 'Basic command center operator with limited permissions',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'incidents:add_notes',
      'major_incidents:view',
      'resources:view',
      'units:view',
      'units:view_location',
      'personnel:view',
      'comms:send_messages',
      'comms:view_history',
      'map:view',
      'map:view_unit_locations',
      'reports:view',
      'logs:view',
      'logs:create',
      'intelligence:view',
    ],
  },

  incident_commander: {
    role: 'incident_commander',
    description: 'Incident command authority with tactical control',
    isCustom: false,
    permissions: [
      'events:view',
      'events:edit',
      'incidents:create',
      'incidents:edit',
      'incidents:view',
      'incidents:assign',
      'incidents:close',
      'incidents:change_severity',
      'incidents:add_notes',
      'incidents:view_sensitive',
      'major_incidents:declare',
      'major_incidents:manage',
      'major_incidents:view',
      'major_incidents:assign_commanders',
      'major_incidents:edit_methan',
      'resources:view',
      'resources:assign',
      'resources:recall',
      'resources:change_status',
      'units:view',
      'units:dispatch',
      'units:recall',
      'units:change_status',
      'units:view_location',
      'personnel:view',
      'personnel:assign',
      'comms:send_messages',
      'comms:broadcast',
      'comms:view_history',
      'comms:emergency_alert',
      'map:view',
      'map:draw',
      'map:edit_drawings',
      'map:share_drawings',
      'map:view_unit_locations',
      'map:view_sensitive_locations',
      'reports:view',
      'reports:create',
      'logs:view',
      'logs:create',
      'notifications:send',
      'notifications:broadcast',
      'intelligence:view',
      'intelligence:create',
    ],
  },

  tactical_commander: {
    role: 'tactical_commander',
    description: 'Tactical operations commander for field operations',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'incidents:edit',
      'incidents:assign',
      'incidents:add_notes',
      'major_incidents:view',
      'major_incidents:manage',
      'resources:view',
      'resources:assign',
      'resources:change_status',
      'units:view',
      'units:dispatch',
      'units:change_status',
      'units:view_location',
      'personnel:view',
      'personnel:assign',
      'comms:send_messages',
      'comms:broadcast',
      'comms:view_history',
      'map:view',
      'map:draw',
      'map:edit_drawings',
      'map:view_unit_locations',
      'reports:view',
      'logs:view',
      'logs:create',
      'notifications:send',
      'intelligence:view',
    ],
  },

  mdt_supervisor: {
    role: 'mdt_supervisor',
    description: 'Mobile data terminal supervisor with field oversight',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'incidents:edit',
      'incidents:add_notes',
      'resources:view',
      'units:view',
      'units:change_status',
      'units:view_location',
      'personnel:view',
      'comms:send_messages',
      'comms:view_history',
      'map:view',
      'map:view_unit_locations',
      'reports:view',
      'logs:view',
      'logs:create',
      'intelligence:view',
      'prfs:view',
      'prfs:print',
    ],
  },

  mdt_officer: {
    role: 'mdt_officer',
    description: 'Mobile data terminal officer for field operations',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'incidents:add_notes',
      'resources:view',
      'units:view',
      'units:view_location',
      'personnel:view',
      'comms:send_messages',
      'comms:view_history',
      'map:view',
      'map:view_unit_locations',
      'reports:view',
      'logs:view',
      'logs:create',
    ],
  },

  resource_manager: {
    role: 'resource_manager',
    description: 'Resource allocation and inventory management',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'resources:create',
      'resources:edit',
      'resources:delete',
      'resources:view',
      'resources:assign',
      'resources:recall',
      'resources:change_status',
      'resources:manage_inventory',
      'units:view',
      'units:view_location',
      'personnel:view',
      'comms:send_messages',
      'comms:view_history',
      'map:view',
      'reports:view',
      'reports:create',
      'logs:view',
      'logs:create',
    ],
  },

  logistics_coordinator: {
    role: 'logistics_coordinator',
    description: 'Logistics and supply chain coordination',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'resources:view',
      'resources:assign',
      'resources:manage_inventory',
      'units:view',
      'personnel:view',
      'comms:send_messages',
      'comms:view_history',
      'map:view',
      'reports:view',
      'reports:create',
      'logs:view',
      'logs:create',
    ],
  },

  intelligence_analyst: {
    role: 'intelligence_analyst',
    description: 'Intelligence gathering and analysis',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'incidents:view_sensitive',
      'major_incidents:view',
      'resources:view',
      'units:view',
      'personnel:view',
      'personnel:view_sensitive',
      'comms:view_history',
      'map:view',
      'map:view_sensitive_locations',
      'reports:view',
      'reports:create',
      'reports:view_sensitive',
      'logs:view',
      'logs:view_audit',
      'intelligence:view',
      'intelligence:create',
      'intelligence:edit',
      'intelligence:delete',
      'intelligence:share',
    ],
  },

  communications_officer: {
    role: 'communications_officer',
    description: 'Communications and messaging specialist',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'units:view',
      'personnel:view',
      'comms:send_messages',
      'comms:broadcast',
      'comms:view_history',
      'comms:manage_channels',
      'comms:emergency_alert',
      'map:view',
      'reports:view',
      'logs:view',
      'logs:create',
      'notifications:send',
      'notifications:broadcast',
      'notifications:manage',
    ],
  },

  training_officer: {
    role: 'training_officer',
    description: 'Training and personnel development',
    isCustom: false,
    permissions: [
      'events:view',
      'personnel:view',
      'personnel:manage_certifications',
      'comms:send_messages',
      'reports:view',
      'reports:create',
      'logs:view',
      'training:view',
      'training:create',
      'training:manage',
      'training:assign',
    ],
  },

  read_only: {
    role: 'read_only',
    description: 'View-only access to most system features',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'resources:view',
      'units:view',
      'personnel:view',
      'comms:view_history',
      'map:view',
      'reports:view',
      'logs:view',
      'intelligence:view',
    ],
  },

  guest: {
    role: 'guest',
    description: 'Limited guest access with minimal permissions',
    isCustom: false,
    permissions: [
      'events:view',
      'incidents:view',
      'map:view',
      'reports:view',
    ],
  },
};

// Permission categories for UI organization
export const PERMISSION_CATEGORIES = {
  'System Administration': [
    'system:manage_users',
    'system:manage_roles',
    'system:manage_permissions',
    'system:view_audit_logs',
    'system:manage_settings',
    'system:manage_integrations',
    'system:backup_restore',
    'system:view_analytics',
  ],
  'Event Management': [
    'events:create',
    'events:edit',
    'events:delete',
    'events:view',
    'events:close',
    'events:reopen',
    'events:assign_personnel',
    'events:manage_resources',
  ],
  'Incident Management': [
    'incidents:create',
    'incidents:edit',
    'incidents:delete',
    'incidents:view',
    'incidents:assign',
    'incidents:close',
    'incidents:reopen',
    'incidents:change_severity',
    'incidents:change_category',
    'incidents:add_notes',
    'incidents:view_sensitive',
    'incidents:export',
  ],
  'Major Incident Management': [
    'major_incidents:declare',
    'major_incidents:manage',
    'major_incidents:view',
    'major_incidents:assign_commanders',
    'major_incidents:close',
    'major_incidents:edit_methan',
  ],
  'Resource Management': [
    'resources:create',
    'resources:edit',
    'resources:delete',
    'resources:view',
    'resources:assign',
    'resources:recall',
    'resources:change_status',
    'resources:manage_inventory',
  ],
  'Unit Management': [
    'units:create',
    'units:edit',
    'units:delete',
    'units:view',
    'units:dispatch',
    'units:recall',
    'units:change_status',
    'units:assign_personnel',
    'units:view_location',
  ],
  'Personnel Management': [
    'personnel:create',
    'personnel:edit',
    'personnel:delete',
    'personnel:view',
    'personnel:assign',
    'personnel:change_status',
    'personnel:view_sensitive',
    'personnel:manage_certifications',
  ],
  'Communications': [
    'comms:send_messages',
    'comms:broadcast',
    'comms:view_history',
    'comms:manage_channels',
    'comms:emergency_alert',
    'comms:priority_override',
  ],
  'Map & Location': [
    'map:view',
    'map:draw',
    'map:edit_drawings',
    'map:delete_drawings',
    'map:share_drawings',
    'map:view_unit_locations',
    'map:view_sensitive_locations',
  ],
  'Reports & Analytics': [
    'reports:view',
    'reports:create',
    'reports:export',
    'reports:view_sensitive',
    'reports:manage_templates',
  ],
  'Activity Logs': [
    'logs:view',
    'logs:create',
    'logs:edit',
    'logs:delete',
    'logs:export',
    'logs:view_audit',
  ],
  'Notifications': [
    'notifications:send',
    'notifications:broadcast',
    'notifications:manage',
  ],
  'Training & Development': [
    'training:view',
    'training:create',
    'training:manage',
    'training:assign',
  ],
  'Intelligence': [
    'intelligence:view',
    'intelligence:create',
    'intelligence:edit',
    'intelligence:delete',
    'intelligence:share',
  ],
  'Patient Report Forms (PRF)': [
    'prfs:view',
    'prfs:print',
    'prfs:export',
    'prfs:edit',
    'prfs:delete',
  ],
} as const;

// Permission descriptions for UI
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  // System
  'system:manage_users': 'Create, edit, and delete user accounts',
  'system:manage_roles': 'Create and modify user roles',
  'system:manage_permissions': 'Assign and revoke permissions',
  'system:view_audit_logs': 'View system audit logs and user activity',
  'system:manage_settings': 'Configure system-wide settings',
  'system:manage_integrations': 'Manage third-party integrations',
  'system:backup_restore': 'Perform system backups and restores',
  'system:view_analytics': 'Access system analytics and metrics',
  
  // Events
  'events:create': 'Create new events',
  'events:edit': 'Modify existing events',
  'events:delete': 'Delete events',
  'events:view': 'View event details',
  'events:close': 'Close active events',
  'events:reopen': 'Reopen closed events',
  'events:assign_personnel': 'Assign personnel to events',
  'events:manage_resources': 'Allocate resources to events',
  
  // Incidents
  'incidents:create': 'Create new incidents',
  'incidents:edit': 'Modify incident details',
  'incidents:delete': 'Delete incidents',
  'incidents:view': 'View incident information',
  'incidents:assign': 'Assign incidents to units/personnel',
  'incidents:close': 'Close incidents',
  'incidents:reopen': 'Reopen closed incidents',
  'incidents:change_severity': 'Change incident severity level',
  'incidents:change_category': 'Change incident category',
  'incidents:add_notes': 'Add notes and comments to incidents',
  'incidents:view_sensitive': 'View sensitive incident information',
  'incidents:export': 'Export incident data',
  
  // Major Incidents
  'major_incidents:declare': 'Declare major incidents',
  'major_incidents:manage': 'Manage major incident operations',
  'major_incidents:view': 'View major incident details',
  'major_incidents:assign_commanders': 'Assign Gold/Silver/Bronze commanders',
  'major_incidents:close': 'Close major incidents',
  'major_incidents:edit_methan': 'Edit METHAN report details',
  
  // Resources
  'resources:create': 'Add new resources to inventory',
  'resources:edit': 'Modify resource details',
  'resources:delete': 'Remove resources from system',
  'resources:view': 'View resource information',
  'resources:assign': 'Assign resources to incidents/events',
  'resources:recall': 'Recall deployed resources',
  'resources:change_status': 'Update resource status',
  'resources:manage_inventory': 'Manage resource inventory levels',
  
  // Units
  'units:create': 'Create new units',
  'units:edit': 'Modify unit details',
  'units:delete': 'Delete units',
  'units:view': 'View unit information',
  'units:dispatch': 'Dispatch units to incidents',
  'units:recall': 'Recall units from assignments',
  'units:change_status': 'Update unit status',
  'units:assign_personnel': 'Assign personnel to units',
  'units:view_location': 'View real-time unit locations',
  
  // Personnel
  'personnel:create': 'Add new personnel records',
  'personnel:edit': 'Modify personnel information',
  'personnel:delete': 'Remove personnel records',
  'personnel:view': 'View personnel information',
  'personnel:assign': 'Assign personnel to units/incidents',
  'personnel:change_status': 'Update personnel status',
  'personnel:view_sensitive': 'View sensitive personnel data',
  'personnel:manage_certifications': 'Manage training certifications',
  
  // Communications
  'comms:send_messages': 'Send messages to users/units',
  'comms:broadcast': 'Send broadcast messages',
  'comms:view_history': 'View communication history',
  'comms:manage_channels': 'Create and manage communication channels',
  'comms:emergency_alert': 'Send emergency alerts',
  'comms:priority_override': 'Override message priority',
  
  // Map
  'map:view': 'View map and locations',
  'map:draw': 'Draw on map (zones, markers, etc.)',
  'map:edit_drawings': 'Edit existing map drawings',
  'map:delete_drawings': 'Delete map drawings',
  'map:share_drawings': 'Share map drawings with others',
  'map:view_unit_locations': 'View unit locations on map',
  'map:view_sensitive_locations': 'View sensitive/classified locations',
  
  // Reports
  'reports:view': 'View reports and analytics',
  'reports:create': 'Generate new reports',
  'reports:export': 'Export reports to file',
  'reports:view_sensitive': 'View sensitive report data',
  'reports:manage_templates': 'Create and edit report templates',
  
  // Logs
  'logs:view': 'View activity logs',
  'logs:create': 'Create log entries',
  'logs:edit': 'Edit log entries',
  'logs:delete': 'Delete log entries',
  'logs:export': 'Export log data',
  'logs:view_audit': 'View audit trail logs',
  
  // Notifications
  'notifications:send': 'Send notifications to users',
  'notifications:broadcast': 'Send system-wide notifications',
  'notifications:manage': 'Manage notification settings',
  
  // Training
  'training:view': 'View training materials',
  'training:create': 'Create training content',
  'training:manage': 'Manage training programs',
  'training:assign': 'Assign training to personnel',
  
  // Intelligence
  'intelligence:view': 'View intelligence reports',
  'intelligence:create': 'Create intelligence reports',
  'intelligence:edit': 'Edit intelligence reports',
  'intelligence:delete': 'Delete intelligence reports',
  'intelligence:share': 'Share intelligence with other agencies',

  // Patient Report Forms (PRF)
  'prfs:view': 'View Patient Report Forms (clinical patient data)',
  'prfs:print': 'Print Patient Report Forms',
  'prfs:export': 'Export PRFs to file (PDF/CSV)',
  'prfs:edit': 'Edit / amend submitted Patient Report Forms',
  'prfs:delete': 'Delete Patient Report Forms',
};

// Helper function to get all permissions for a role
export function getRolePermissions(role: UserRole): Permission[] {
  return DEFAULT_ROLE_PERMISSIONS[role]?.permissions || [];
}

// Helper function to check if a user has a specific permission
export function hasPermission(
  userRole: UserRole,
  customPermissions: Permission[] | undefined,
  requiredPermission: Permission
): boolean {
  const rolePermissions = getRolePermissions(userRole);
  const allPermissions = customPermissions 
    ? [...rolePermissions, ...customPermissions]
    : rolePermissions;
  
  return allPermissions.includes(requiredPermission);
}

// Helper function to check if a user has any of the required permissions
export function hasAnyPermission(
  userRole: UserRole,
  customPermissions: Permission[] | undefined,
  requiredPermissions: Permission[]
): boolean {
  return requiredPermissions.some(permission => 
    hasPermission(userRole, customPermissions, permission)
  );
}

// Helper function to check if a user has all required permissions
export function hasAllPermissions(
  userRole: UserRole,
  customPermissions: Permission[] | undefined,
  requiredPermissions: Permission[]
): boolean {
  return requiredPermissions.every(permission => 
    hasPermission(userRole, customPermissions, permission)
  );
}
